import { useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import {
  formatCurrency,
  formatIban,
  formatPercent,
  formatTransactionAmount,
  normalizeIban,
} from 'common/utils/formatUtils';
import { format } from 'date-fns';
import {
  FinancialProductType,
  LoanProductFragment,
  useLoanProductQuery,
  useUpdateLoanProductMutation,
} from 'generated/graphql';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductDetail } from '../ProductDetail/ProductDetail';
import { QRCodeDialog, QRCodeDialogProps } from '../QRCodeDialog/QRCodeDialog';
import { QRCodeMissingDataDialog, QRCodeMissingDataDialogProps } from '../QRCodeDialog/QRCodeMissingDataDialog';
import { ButtonRowType, DetailRowType, Section } from '../types';
import { ProductDetailDialogProps } from '../useProductDetail';
import { mapLoanProductToInput } from './utils';

export const LoanProductDetail: React.FC<ProductDetailDialogProps> = ({ id, ...props }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();

  const { data } = useLoanProductQuery({
    variables: {
      id,
    },
  });

  const qrCodeDialog = useDialog<QRCodeDialogProps, void>(QRCodeDialog, {
    instanceId: 'qr-code-dialog',
    tmpNavigable: true,
  });

  const qrCodeMissingDataDialog = useDialog<QRCodeMissingDataDialogProps, void>(QRCodeMissingDataDialog, {
    instanceId: 'qr-code-missing-data-dialog',
  });

  const handleQRPayment = () => {
    const iban = data?.detail.paymentInfo?.ibanForPayment;
    const amount = data?.detail.paymentPeriod?.expectedAmount?.amount;
    if (iban && amount) {
      qrCodeDialog.create({
        amount,
        constantSymbol: data?.detail.paymentInfo?.constantSymbol ?? undefined,
        currency: data?.detail.paymentPeriod?.expectedAmount?.currency ?? 'EUR',
        iban,
        specificSymbol: data?.detail.paymentInfo?.specificSymbol ?? undefined,
        variableSymbol: data?.detail.paymentInfo?.variableSymbol ?? undefined,
      });
    } else {
      qrCodeMissingDataDialog.create({});
    }
  };

  const periodicityOptions = usePeriodicityOptions();

  const content: Section<LoanProductFragment, Path<LoanProductFragment>>[] = [
    {
      rows: [
        {
          inputType: 'text',
          label: t('products:loan.detail.institutionName'),
          path: 'institutionName',
          skip: !!data?.detail.institution?.id,
        } as DetailRowType<LoanProductFragment, 'institutionName'>,
        {
          inputType: 'text',
          label: t('products:loan.detail.name'),
          path: 'name',
        } as DetailRowType<LoanProductFragment, 'name'>,
        {
          inputType: 'text',
          label: t('products:loan.detail.contractNumber'),
          path: 'contractNumber',
        } as DetailRowType<LoanProductFragment, 'contractNumber'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:loan.detail.contractSignDate'),
          path: 'contractSignDate',
        } as DetailRowType<LoanProductFragment, 'contractSignDate'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:loan.detail.contractEndDate'),
          path: 'contractEndDate',
        } as DetailRowType<LoanProductFragment, 'contractEndDate'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          formatter: (value: number) =>
            formatTransactionAmount(value, i18n.language, 2, data?.detail.paymentPeriod?.expectedAmount?.currency),
          inputEndAdornment: data?.detail.paymentPeriod?.expectedAmount?.currency,
          inputType: 'amount',
          label: t('products:loan.detail.instalment'),
          // we are storing paymentPeriod.expectedAmount.amount as negative number
          // but we want user to type a positive equivalent
          parse: (value) => -value,
          path: 'paymentPeriod.expectedAmount.amount',
          transform: (value) => -value,
        } as DetailRowType<LoanProductFragment, 'paymentPeriod.expectedAmount.amount'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:loan.detail.nextPayment'),
          path: 'paymentPeriod.expectedDate',
        } as DetailRowType<LoanProductFragment, 'paymentPeriod.expectedDate'>,
        {
          formatter: (value) =>
            periodicityOptions.find(
              (option) =>
                option.value.periodCount === value.periodCount && option.value.periodTypeCode === value.periodTypeCode,
            )?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:loan.detail.periodType'),
          options: periodicityOptions,
          parse: (value) => ({
            expectedAmount: data?.detail.paymentPeriod?.expectedAmount,
            expectedDate: data?.detail.paymentPeriod?.expectedDate,
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
          path: 'paymentPeriod',
          transform: (value) => ({
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
        } as DetailRowType<LoanProductFragment, 'paymentPeriod'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          inputType: 'variableSymbol',
          label: t('products:loan.detail.variableSymbol'),
          path: 'paymentInfo.variableSymbol',
        } as DetailRowType<LoanProductFragment, 'paymentInfo.variableSymbol'>,
        {
          inputType: 'specificSymbol',
          label: t('products:loan.detail.specificSymbol'),
          path: 'paymentInfo.specificSymbol',
        } as DetailRowType<LoanProductFragment, 'paymentInfo.specificSymbol'>,
        {
          inputType: 'constantSymbol',
          label: t('products:loan.detail.constantSymbol'),
          path: 'paymentInfo.constantSymbol',
        } as DetailRowType<LoanProductFragment, 'paymentInfo.constantSymbol'>,
        {
          formatter: formatIban,
          inputType: 'iban',
          label: t('products:loan.detail.ibanForPayment'),
          parse: normalizeIban,
          path: 'paymentInfo.ibanForPayment',
        } as DetailRowType<LoanProductFragment, 'paymentInfo.ibanForPayment'>,
        {
          color: 'blue.dark',
          label: t('products:loan.detail.qrCode'),
          onClick: handleQRPayment,
        } as ButtonRowType,
      ],
      title: t('products:loan.detail.paymentInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:loan.detail.amount'),
          path: 'amount.amount',
        } as DetailRowType<LoanProductFragment, 'amount.amount'>,
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:loan.detail.balance'),
          path: 'balance.amount',
        } as DetailRowType<LoanProductFragment, 'balance.amount'>,
        {
          formatter: (value: number) => formatPercent(value, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.percent'),
          inputType: 'number',
          label: t('products:loan.detail.interestRate'),
          parse: (value: number) => value / 100,
          path: 'interestRate',
          transform: (value: number) => value * 100,
        } as DetailRowType<LoanProductFragment, 'interestRate'>,
        {
          formatter: (value: number) => formatPercent(value, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.percent'),
          inputType: 'number',
          label: t('products:loan.detail.annualPercentageRate'),
          parse: (value: number) => value / 100,
          path: 'annualPercentageRate',
          transform: (value: number) => value * 100,
        } as DetailRowType<LoanProductFragment, 'annualPercentageRate'>,
      ],
      title: t('products:loan.detail.financialDetails.title', {
        date: format(data?.detail.financialDetailsUpdatedAt ?? new Date(), 'd. M. yyyy'),
      }),
    },
  ];

  const [updateProduct] = useUpdateLoanProductMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CalendarEventInstances', 'Notifications', 'NotificationsCount'],
  });

  const handleUpdate = async (value: LoanProductFragment) => {
    await updateProduct({ variables: { id, updatedProduct: mapLoanProductToInput(value) } });
  };

  return (
    <ProductDetail<LoanProductFragment>
      {...props}
      content={content}
      data={data?.detail}
      institution={data?.detail.institution ?? undefined}
      onUpdate={handleUpdate}
      title={data?.detail.name ?? t('products:loan.detail.title')}
      type={FinancialProductType.Loan}
    />
  );
};
